import { Stack, Typography } from "@mui/material";
import { TreeViewBaseItem } from "@mui/x-tree-view/models";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { MapEditorProtocolTreeFragment } from "./fragments.generated";

interface MapEditorProtocolTreeProps {
    onChange: (protocolVisitIds: string[]) => void;
    data?: MapEditorProtocolTreeFragment | null;
    value: string[];
}

export function MapEditorProtocolTree({ onChange, data, value }: MapEditorProtocolTreeProps) {
    const { t } = useTranslation("mapEditor");

    const elements = useMemo(() => mapToTreeNodes(data?.plannerAreas ?? [], t), [data, t]);
    const typeTable = useMemo(() => mapToTypeTable(data?.plannerAreas ?? []), [data]);

    // If all descendants are selected, also select all of its parents. Assume `value` only contains leaf nodes
    const selectedItems = useMemo(() => addParentIds(elements, value), [elements, value]);

    // If an area/ protocol is selected, also select all of its descendants
    const handleSelectedItemsChange = (_: unknown, selection: string[]) => {
        const nextSelection = selection.filter(x => typeTable.get(x) === "ProtocolVisit");

        onChange([...new Set(nextSelection)]);
    };

    return (
        <Stack direction="column" rowGap={1}>
            <Typography variant="overline" sx={{ paddingInline: 2 }}>
                {t("protocolTree.label")}
            </Typography>

            <RichTreeView
                checkboxSelection
                multiSelect
                items={elements}
                onSelectedItemsChange={handleSelectedItemsChange}
                selectionPropagation={{
                    descendants: true,
                    parents: true,
                }}
                selectedItems={selectedItems}
            />
        </Stack>
    );
}

type PlannerAreas = MapEditorProtocolTreeFragment["plannerAreas"];
type TypeTable = Map<string, "Protocol" | "ProtocolVisit">;

function mapToTypeTable(plannerAreas: PlannerAreas): TypeTable {
    const typeTable: TypeTable = new Map();

    plannerAreas.forEach(plannerArea => {
        (plannerArea.protocols ?? []).forEach(protocol => {
            typeTable.set(protocol.id, "Protocol");
        });

        (plannerArea.visits ?? []).forEach(visit => {
            if (visit.protocolVisit) {
                typeTable.set(visit.protocolVisit.id, "ProtocolVisit");
            }
        });
    });

    return typeTable;
}

function addParentIds(elements: TreeViewBaseItem[], selectedLeafIds: string[]) {
    const selected = new Set(selectedLeafIds);

    function isSelected(el: TreeViewBaseItem) {
        return !el.children?.length ? selected.has(el.id) : el.children.every(isSelected);
    }

    function collect(el: TreeViewBaseItem) {
        if (isSelected(el)) selected.add(el.id);
        el.children?.forEach(collect);
    }

    elements.forEach(collect);
    return [...selected];
}

function mapToTreeNodes(plannerAreas: PlannerAreas, t: (key: string) => string): TreeViewBaseItem[] {
    const protocolMap = new Map<
        string,
        { id: string; name: string; protocolVisits: Map<string, { id: string; label: string }> }
    >();

    plannerAreas.forEach(area => {
        (area.protocols ?? []).forEach(areaProtocol => {
            if (!protocolMap.has(areaProtocol.protocol.id)) {
                protocolMap.set(areaProtocol.protocol.id, {
                    id: areaProtocol.protocol.id,
                    name: areaProtocol.protocol.name,
                    protocolVisits: new Map(),
                });
            }

            const entry = protocolMap.get(areaProtocol.protocol.id);
            if (!entry) return;

            (area.visits ?? [])
                .filter(visit => visit.protocolId === areaProtocol.protocol.id)
                .forEach(visit => {
                    if (!visit.protocolVisit) return;
                    if (entry.protocolVisits.has(visit.protocolVisit.id)) return;

                    const label = visit.protocolVisit.name
                        ? `${visit.protocolVisit.name} ${visit.protocolVisit.number}`
                        : `${t("protocolTree.visit")} ${visit.protocolVisit.number}`;

                    entry.protocolVisits.set(visit.protocolVisit.id, {
                        id: visit.protocolVisit.id,
                        label,
                    });
                });
        });
    });

    return [...protocolMap.values()]
        .filter(protocol => protocol.protocolVisits.size > 0)
        .map(protocol => ({
            id: protocol.id,
            label: protocol.name,
            children: [...protocol.protocolVisits.values()].map(pv => ({
                id: pv.id,
                label: pv.label,
            })),
        }));
}
